/**
 * Created by katarinababic on 18.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTrans } from '../../Store/System/LocalizationStore';
import { WeaviateSuggestionResult, WeaviateSuggestionResultApi } from '../../Model/Suggestions/WeaviateSuggestionResult';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { HomeScreenSuggestionsRendererSwitch } from './HomeScreenSuggestionsRendererSwitch';
import { Theme } from '../../Theme/Theme';
import { Translation } from '../../Components/Translation';

export type HomeScreenSuggestionsProps = {};

type SuggestionCategory = 'movement' | 'mental' | 'nutrition';

const SUGGESTION_CATEGORIES: SuggestionCategory[] = ['movement', 'mental', 'nutrition'];

const getCategoryIcon = (category: SuggestionCategory): string => {
  switch (category) {
    case 'movement':
      return '🏃';
    case 'mental':
      return '🧘';
    case 'nutrition':
      return '🥗';
    default:
      return '✨';
  }
};

const getCategoryLabel = (category: SuggestionCategory, t: any): string => {
  switch (category) {
    case 'movement':
      return t('suggestions.category.movement', { defaultValue: 'Movement' });
    case 'mental':
      return t('suggestions.category.mental', { defaultValue: 'Mental Health' });
    case 'nutrition':
      return t('suggestions.category.nutrition', { defaultValue: 'Nutrition' });
    default:
      return category;
  }
};

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SectionHeader = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 8px;
  color: ${Theme.Colors.textColor};
`;

const HintText = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const SuggestionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingCard = styled.div`
  background: white;
  border-radius: 16px;
  height: 100px;
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 50%, transparent 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }
`;

const LoadingIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  z-index: 1;
`;

const LoadingContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1;
`;

const LoadingLine = styled.div<{ width?: string }>`
  height: 12px;
  width: ${({ width }) => width || '100%'};
  background: ${Theme.Colors.lightestGrey};
  border-radius: 6px;
`;

const EmptyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const EmptyText = styled.div`
  flex: 1;
`;

const EmptyTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const EmptySubtitle = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin: 0;
`;

export const HomeScreenSuggestions: React.FC<HomeScreenSuggestionsProps> = observer(() => {
  const { t } = useTrans();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

  const {
    data: suggestions = [],
    refetch,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: [`coachTabSuggestions:${dayjs().format('YYYYMMDD')}`],
    queryFn: () => WeaviateSuggestionResultApi.daily(),
    initialData: [],
  });

  React.useEffect(() => {
    if (!isLoading && !isFetching && suggestions.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [isLoading, isFetching, suggestions]);

  const suggestionsByCategory = React.useMemo(() => {
    const map = new Map<string, WeaviateSuggestionResult>();
    suggestions?.forEach((s) => {
      if (s && !map.has(s.category)) {
        map.set(s.category, s);
      }
    });
    return map;
  }, [suggestions]);

  const showInitialLoading = isLoading || (isFetching && !hasLoadedOnce);

  return (
    <span>
      <SectionHeader>
        <Translation
          i18nKey="home.dailySuggestions"
          defaultValue={`<span style="color: #00dda2">Daily</span> Suggestions`}
        />
      </SectionHeader>
      <HintText>
        {t('coach_tab.suggestionsHint', {
          defaultValue: 'The suggestions are based on your answers and adjust according to the time of the day.',
        })}
      </HintText>
      <SuggestionsGrid>
        {SUGGESTION_CATEGORIES.map((category) => {
          const suggestion = suggestionsByCategory.get(category);

          if (showInitialLoading) {
            return (
              <LoadingCard key={`loading-${category}`}>
                <LoadingIcon>{getCategoryIcon(category)}</LoadingIcon>
                <LoadingContent>
                  <LoadingLine width="40%" />
                  <LoadingLine width="80%" />
                </LoadingContent>
              </LoadingCard>
            );
          }

          if (!suggestion) {
            return (
              <EmptyCard key={`empty-${category}`}>
                <EmptyIcon>{getCategoryIcon(category)}</EmptyIcon>
                <EmptyText>
                  <EmptyTitle>{getCategoryLabel(category, t)}</EmptyTitle>
                  <EmptySubtitle>
                    {t('suggestions.noSuggestion', { defaultValue: 'No suggestion available right now' })}
                  </EmptySubtitle>
                </EmptyText>
              </EmptyCard>
            );
          }

          return (
            <HomeScreenSuggestionsRendererSwitch
              key={`${category}-${suggestion.contentId}`}
              suggestion={suggestion}
              onRefresh={() => refetch()}
            />
          );
        })}
      </SuggestionsGrid>
    </span>
  );
});
